import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

/** Generic GET hook for the provider/report composite endpoints. Refetches when params change. */
function useFetch<T>(url: string, params?: Record<string, string | undefined>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stable key so the effect only re-runs on real param changes.
  const paramsKey = JSON.stringify(params ?? {});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const parsed = JSON.parse(paramsKey) as Record<string, string | undefined>;
      const clean: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (v) clean[k] = v;
      }
      const res = await axiosInstance.get(url, { params: clean });
      setData((res.data?.data ?? res.data) as T);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [url, paramsKey]);

  useEffect(() => { void load(); }, [load]);
  return { data, loading, error, reload: load };
}

export interface ProviderDashboardData {
  business: { id: string; name: string; owner: string };
  kpis: { total_tokens: number; completed: number; waiting: number; in_progress: number; avg_wait: string };
  overview: { waiting: number; in_progress: number; completed: number; skipped: number; cancelled: number };
  trend_labels: string[];
  trend: number[];
  next_appointments: Array<Record<string, any>>;
}
export const useProviderDashboard = (businessId?: string, from?: string, to?: string) =>
  useFetch<ProviderDashboardData>(API_ENDPOINTS.PROVIDER_DASHBOARD, { business_id: businessId, from, to });

export interface TodaysQueueData {
  business: { id: string; name: string; owner: string };
  ref_date: string | null;
  items: Array<Record<string, any>>;
  counts: Record<string, number>;
}
export const useTodaysQueue = (businessId?: string) =>
  useFetch<TodaysQueueData>(API_ENDPOINTS.TODAYS_QUEUE, { business_id: businessId });

export const useProviderReports = (businessId?: string, from?: string, to?: string) =>
  useFetch<Record<string, any>>(API_ENDPOINTS.REPORTS_PROVIDER, { business_id: businessId, from, to });
export const useAdminReports = (from?: string, to?: string) =>
  useFetch<Record<string, any>>(API_ENDPOINTS.REPORTS_ADMIN, { from, to });
export interface ReportBusiness { id: string; name: string; city: string | null; state_name: string | null }
export const useReportBusinesses = () =>
  useFetch<Array<ReportBusiness>>(API_ENDPOINTS.REPORTS_BUSINESSES);
